import type { FilterRuleModel } from '@/shared/generated'

import memoizeOne from 'memoize-one'
import * as qs from 'qs'

/**
 * Transforms query string to object with comma separated arrays
 * @example  '?a=1&b=2,3' => { a: 1, b: [2, 3] }
 * @param search - location.search string to be parsed
 * @returns - object with query params
 */
export const toQueryObject = (search: string) =>
	qs.parse(search, { ignoreQueryPrefix: true, comma: true })

/**
 * Transforms object to query string with comma separated arrays and without prefix
 * @example  { a: 1, b: [2, 3] } => '?a=1&b=2,3'
 * @param options - object to be converted to query string
 * @returns - query string
 */
export const toQueryString = (options: object) =>
	qs.stringify(options, {
		arrayFormat: 'comma',
		addQueryPrefix: true,
		encode: false,
	})

export const getLocationQuery = memoizeOne((search) =>
	qs.parse(search, { ignoreQueryPrefix: true, comma: true })
)

/**
 * Function to decode complex query from url
 * @param encodedString - encoded string to decode
 * @returns - decoded object
 */
export function decodeComplexQuery(encodedString: string) {
	try {
		return JSON.parse(decodeURIComponent(atob(encodedString)))
	} catch (e) {
		return undefined
	}
}

/**
 * Function to encode complex query to url
 * @param decodedObject - decoded object to encode
 * @returns - encoded string
 */
export function encodeComplexQuery(decodedObject: object) {
	try {
		return btoa(encodeURIComponent(JSON.stringify(decodedObject)))
	} catch (e) {
		return undefined
	}
}

/**
 * Function to get filters query string
 * @param filters - filters to be encoded or undefined if no filters
 * @returns - filters query string
 */
export const getFiltersQueryString = (filters?: FilterRuleModel[]) =>
	qs.stringify(getFiltersQueryObject(filters), {
		addQueryPrefix: true,
		encode: false,
	})
/**
 * Function to get filters query string
 * @param filters - filters to be encoded or undefined if no filters
 * @returns - filters query object
 */
export const getFiltersQueryObject = (filters?: FilterRuleModel[]) => ({
	qf: filters?.length
		? encodeComplexQuery({
				filters,
			})
		: undefined,
})
